import React from 'react';
import { Box, Grid, SxProps, Theme, Typography } from '@mui/material';

import clsx from 'clsx';
import chunk from 'lodash/chunk';

import mixinSx from '@watershed/style/mixinSx';

const dlSx: SxProps<Theme> = {
  margin: 0,
};

const rowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'baseline',
  '&.showBorders + &': {
    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
  },
};

const dtSx: SxProps<Theme> = {
  flexShrink: 0,
  paddingRight: 2,
  paddingTop: 1,
  paddingBottom: 1,
  overflowWrap: 'break-word',
  '&.dense': {
    fontSize: 13,
    lineHeight: 1.25,
    paddingTop: 1,
    paddingBottom: 1,
  },
};

const ddSx: SxProps<Theme> = {
  margin: 0,
  flexGrow: 1,
  overflow: 'auto',
  paddingTop: 1,
  paddingBottom: 1,
  '&.dense': {
    fontSize: 13,
    lineHeight: 1.25,
    paddingTop: 1,
    paddingBottom: 1,
  },
};

export interface IRenderValue {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (key: string, value: any, data: Map<string, any>): React.ReactNode | null;
}

const preJsonRenderValue: IRenderValue = (key, value, data) => {
  return (
    <Box
      component="pre"
      sx={{
        fontFamily: (theme: Theme) => theme.typography.fontFamilyMonospace,
      }}
    >
      {JSON.stringify(value, null, 2)}
    </Box>
  );
};

const defaultRenderValue: IRenderValue = (key, value, data) => {
  if (React.isValidElement(value)) return value;
  if (value !== null && typeof value === 'object') {
    return preJsonRenderValue(key, value, data);
  }
  if (value !== null && typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  return value;
};

/**
 * A list of key-value pairs presented as rows.
 */
export default function KeyValueRows({
  data,
  renderValue = defaultRenderValue,
  displayNullValues = false,
  displayUndefinedValues = false,
  keyWidth = 200,
  valueMaxHeight = 240,
  paddingX = 0,
  isDense = false,
  columnCount = 1,
  includeBorders = true,
}: {
  /**
   * Key-value pairs are provided as a Map so the caller has control over the
   * order in which pairs will be rendered.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: Map<string, any>;
  /**
   * A function for customizing how values are rendered. For each row, receives
   * the row's key, row's value, and the Map passed in as `data`. To skip
   * rendering that row altogether, return `null`. Otherwise, return a React
   * node.
   */
  renderValue?: IRenderValue;
  /**
   * If `false`, items in `data` will be filtered out if their value is
   * `null`.
   */
  displayNullValues?: boolean;
  /**
   * If `false`, items in `data` will be filtered out if their value is
   * `undefined`.
   */
  displayUndefinedValues?: boolean;
  /**
   * The width of for name elements.
   */
  keyWidth?: string | number;
  /**
   * The maximum height for value elements. Overflow will scroll.
   */
  valueMaxHeight?: string | number;
  /**
   * Padding for the left and right of each row. This can be used to extend the
   * border between rows beyond the contents.
   */
  paddingX?: string | number;
  /**
   * If true, the rendering has smaller text and less whitespace.
   */
  isDense?: boolean;
  /**
   * Key-value pairs will be divided evenly across the columns. This division
   * only accounts for the key-value pair count, not for the height of rendered
   * values; so if rendered values can have significantly different heights,
   * the columns won't appear balanced.
   */
  columnCount?: 1 | 2 | 3;
  /**
   * If false, there are no borders between rows.
   */
  includeBorders?: boolean;
}) {
  const rows = Array.from(data)
    .filter(
      ([k, v]) =>
        (displayNullValues || v !== null) &&
        (displayUndefinedValues || v !== undefined) &&
        k !== '__typename'
    )
    .flatMap(([k, v]) => {
      const rendered = renderValue(k, v, data);
      if (rendered == null) {
        return [];
      }
      return [
        <Box
          key={k}
          className={clsx(isDense && 'dense', includeBorders && 'showBorders')}
          sx={rowSx}
        >
          <Typography
            component="dt"
            variant="body2"
            className={clsx(isDense && 'dense')}
            sx={mixinSx(dtSx, { width: keyWidth, paddingLeft: paddingX })}
          >
            {k.trim()}
          </Typography>
          <Typography
            component="dd"
            variant="body1"
            className={clsx(isDense && 'dense')}
            sx={mixinSx(ddSx, {
              maxHeight: valueMaxHeight,
              paddingRight: paddingX,
            })}
          >
            {rendered}
          </Typography>
        </Box>,
      ];
    });

  const rowsByColumn = chunk(rows, Math.ceil(rows.length / columnCount));
  const renderedColumns = rowsByColumn.map((columnRows, index) => {
    return (
      <Grid key={index} size={12 / columnCount}>
        <Box component="dl" sx={dlSx}>
          {columnRows}
        </Box>
      </Grid>
    );
  });

  return (
    <Grid container spacing={4}>
      {renderedColumns}
    </Grid>
  );
}
